import type { DefaultMenuItem, GetContextMenuItemsParams, IRowNode, MenuItemDef, RowDragEndEvent } from 'ag-grid-enterprise'
import consola from 'consola'
import { defineStore } from 'pinia'
import { firstValueFrom } from 'rxjs'
import type { Ref } from 'vue'
import { reactive, ref, watch } from 'vue'
import {
  checkNodeIsDebitOrCredit,
  checkNoteIsDebitCredit,
  convertL3UnspecifiedToDebitCredit,
  removeDebitCreditFromKey,
  rephaseDuplicatedkey,
  updateBsMapListAndRemoveFromPnl,
  updateCurrentMapListAndRemoveFromOther,
  updatePnlMapListAndRemoveFromBs,
} from '~/composables/acm-action-button'
import { type AcmGridActionButtonStrategy, CreditDebitActionButton, DummyActionButton, IntercompanyActionButton, NetDebtActionButton, TagsActionButton } from '~/composables/acm-action-button-strategies'
import type { GenericPayloadResponse, MultiGenericPayloadResponse } from '~/protos/gen/stcs_commons.entity'
import { type StcsCompAccMapping, type StcsCompAccMappingFstmt, type StcsCompAccMappingL2, type StcsCompAccMappingLevel3, StcsCompAccMappingMeta } from '~/protos/gen/stcs_comp_acc_mapping.entity'
import { StcsFpnaTypeEnum, StcsFstmtEnum, StcsResCode } from '~/protos/gen/stcs_enums.entity'
import type { StcsI18nLabelMap } from '~/protos/gen/stcs_i18n.entity'
import type { StcsOrgFsdFlatItem } from '~/protos/gen/stcs_org_fsd_flat.entity'
import type { DmCompAcmConfirmRequest, DmCompAcmGetRequest, DmCompAcmSaveDraftRequest } from '~/protos/gen/stcs_wapp_svc_dm'
import type { OnboardingCompleteAccMappingRequest } from '~/protos/gen/stcs_wapp_svc_onboarding'
import { cmwDmCompAcmConfirmRequest, cmwDmCompAcmGetDraftRequest, cmwDmCompAcmGetRequest, cmwDmCompAcmSaveDraftRequest, cmwOnboardingCompleteAccMappingRequest } from '~/services/cmw.service'
import { buildHierarchy } from '~/services/commons.service'
import { useCtxStore } from '~/stores/ctx.store'
import { useFsdStore as importedFsdStore } from '~/stores/fsd.store'
import { useNuxtApp, useRoute } from '#imports'

export interface CustomTreeNode {
  label: string | undefined
  data: StcsOrgFsdFlatItem
  children: CustomTreeNode[]
}

export enum MappingStep {
  EDIT = 0,
  CONFIRM = 1,
  LOADING = 2,
  CONFIRM_LOADING = 3,
  NOT_FOUND = 4,
}

type MapHistoryItem = {
  pnlList: StcsOrgFsdFlatItem[]
  bsList: StcsOrgFsdFlatItem[]
  pnlMapList: StcsCompAccMappingFstmt
  bsMapList: StcsCompAccMappingFstmt
  i18n_map: { [key: string]: StcsI18nLabelMap }
}

type LastActionMapList = {
  activeIndex: number
  lastAction: MapHistoryItem[]
}

export const useAcmStore = defineStore(
  'acm-store',
  () => {
    const { $tolgee } = useNuxtApp()

    const pnlList = ref<StcsOrgFsdFlatItem[]>([])
    const bsList = ref<StcsOrgFsdFlatItem[]>([])
    const pnlActionButtonStrategies: AcmGridActionButtonStrategy[] = [new TagsActionButton(), new DummyActionButton(), new DummyActionButton(), new IntercompanyActionButton()]
    const bsActionButtonStrategies: AcmGridActionButtonStrategy[] = [new TagsActionButton(), new CreditDebitActionButton(), new NetDebtActionButton(), new IntercompanyActionButton()]
    const activeFinancialStatement = ref<StcsFstmtEnum>(StcsFstmtEnum.SFSE_PNL)
    const i18n_map = ref<{ [key: string]: StcsI18nLabelMap }>({})
    const i18n_custom_key = ref<string>('')
    const pnlMapList = ref<StcsCompAccMappingFstmt>()
    const bsMapList = ref<StcsCompAccMappingFstmt>()
    const lastActionMapList = ref<LastActionMapList>()
    const isdraft = ref(false)
    const isDataReady = ref(false)
    const accMappingData = reactive<StcsCompAccMapping>({
      id: '',
      version: '',
      meta: StcsCompAccMappingMeta.create(),
      pnl: pnlMapList.value || ({} as StcsCompAccMappingFstmt),
      bs: bsMapList.value || ({} as StcsCompAccMappingFstmt),
      i18n_map: i18n_map.value,
      acm_std_acc_map: {},
      i18n_custom_key: '',
      is_draft: true,
    })
    const nonCatFlag = ref<{ pnl: boolean; bs: boolean }>({
      pnl: false,
      bs: false,
    })
    const route = useRoute()

    // Watcher for pnlMapList and bsMapList to update acmHasNonCat
    watch(
      [pnlMapList, bsMapList],
      ([newPnl, newBs]) => {
        nonCatFlag.value = {
          pnl: newPnl ? Object.keys(newPnl.acm_map_level_2 || {}).some((key) => key.endsWith('level_2_pnl_non_categorized') && Object.keys(newPnl.acm_map_level_2[key]?.acm_map_level_3 || {}).length > 0) : false,
          bs: newBs ? Object.keys(newBs.acm_map_level_2 || {}).some((key) => key.endsWith('level_2_bs_non_categorized') && Object.keys(newBs.acm_map_level_2[key]?.acm_map_level_3 || {}).length > 0) : false,
        }
      },
      { deep: true, immediate: true },
    )
    const mode = ref<MappingStep | undefined>(undefined)
    const fsdStore = importedFsdStore()

    function isFinancialStatementActive(finStmt: StcsFstmtEnum): boolean {
      return activeFinancialStatement.value === finStmt
    }

    function setFinancialStatement(finStmt: StcsFstmtEnum) {
      if (activeFinancialStatement.value !== finStmt) {
        activeFinancialStatement.value = finStmt
      }
    }

    function getMappingItem(node: IRowNode): StcsCompAccMappingLevel3 | undefined {
      const listMaps: Record<StcsFstmtEnum, StcsCompAccMappingFstmt | undefined> = {
        [StcsFstmtEnum.SFSE_NONE]: undefined,
        [StcsFstmtEnum.SFSE_PNL]: pnlMapList.value,
        [StcsFstmtEnum.SFSE_BS]: bsMapList.value,
        [StcsFstmtEnum.SFSE_CF]: undefined,
        [StcsFstmtEnum.UNRECOGNIZED]: undefined,
      }

      const selectedMap = listMaps[activeFinancialStatement.value]

      if (!selectedMap) {
        return undefined
      }

      return selectedMap?.acm_map_level_2?.[node.data.path_level?.[node.data.path_level.length - 2]]?.acm_map_level_3?.[removeDebitCreditFromKey(node.data.row_id)]
    }

    function getMappingLists() {
      const listMaps: Record<StcsFstmtEnum, StcsCompAccMappingFstmt | undefined> = {
        [StcsFstmtEnum.SFSE_NONE]: undefined,
        [StcsFstmtEnum.SFSE_PNL]: pnlMapList.value,
        [StcsFstmtEnum.SFSE_BS]: bsMapList.value,
        [StcsFstmtEnum.SFSE_CF]: undefined,
        [StcsFstmtEnum.UNRECOGNIZED]: undefined,
      }

      const selectedMap = listMaps[activeFinancialStatement.value]

      return Object.values(selectedMap?.acm_map_level_2 ?? {})?.reduce((acc: { [key: string]: StcsCompAccMappingLevel3 }, item) => {
        Object.entries(item.acm_map_level_3 ?? {}).forEach(([key, value]) => {
          acc[key] = value
        })

        return acc
      }, {})
    }

    /**
     * Add a row to the specified financial statement at the given index
     * @param row The row data to insert
     * @param insertIndex The index at which to insert the row (defaults to end of array if invalid)
     * @param financialStatement The financial statement to add the row to
     * @param relativeToRowId Optional row ID to insert relative to (before or after)
     * @param insertAfter If relativeToRowId is provided, whether to insert after (true) or before (false) that row
     */
    function addRow(row: StcsOrgFsdFlatItem, financialStatement: StcsFstmtEnum, relativeToRowId?: string, insertIndex = -1, insertAfter = false) {
      const listRefs: Record<StcsFstmtEnum, Ref<StcsOrgFsdFlatItem[]> | undefined> = {
        [StcsFstmtEnum.SFSE_NONE]: undefined,
        [StcsFstmtEnum.SFSE_PNL]: pnlList,
        [StcsFstmtEnum.SFSE_BS]: bsList,
        [StcsFstmtEnum.SFSE_CF]: undefined,
        [StcsFstmtEnum.UNRECOGNIZED]: undefined,
      }

      // Get the target list reference and value
      const targetRef = listRefs[financialStatement]
      if (!targetRef?.value) {
        return
      }

      const targetList = targetRef.value
      const rowExists = targetList.some((item) => item.row_id === row.row_id)

      if (rowExists) {
        return
      }
      // Determine insertion index
      let finalInsertIndex = insertIndex

      if (relativeToRowId) {
        const relativeIndex = targetList.findIndex((item) => item.row_id === relativeToRowId)
        if (relativeIndex !== -1) {
          finalInsertIndex = insertAfter ? relativeIndex + 1 : relativeIndex
        }
      }

      // Ensure index is valid (if negative or too large)
      finalInsertIndex = finalInsertIndex < 0 ? targetList.length : finalInsertIndex
      finalInsertIndex = Math.min(finalInsertIndex, targetList.length)

      // Update the list with the new row inserted
      const newArr = [...targetList]
      newArr.splice(finalInsertIndex, 0, row)
      targetRef.value = newArr
    }

    function deleteCascadingReferences(targetId: string, arrays: { [key in StcsFstmtEnum]: StcsOrgFsdFlatItem[] }, currentFinancialStatement?: StcsFstmtEnum, arraysToSearch?: StcsFstmtEnum[]) {
      const arrayNames = arraysToSearch !== undefined && arraysToSearch.length > 0 ? arraysToSearch : [StcsFstmtEnum.SFSE_PNL, StcsFstmtEnum.SFSE_BS, StcsFstmtEnum.SFSE_CF]

      // Set to track IDs that should be deleted
      const idsToDelete = new Set()

      // Recursive function to find all references
      function findReferencesToDelete(id: string) {
        // Find all items that reference this id
        arrayNames.forEach((arrayName: StcsFstmtEnum) => {
          let searchForMirrorItem = true
          if (currentFinancialStatement === arrayName) {
            // If we provide a financial statement, then DO NOT search for a mirror item within its array because it would delte mirror L2 items
            searchForMirrorItem = false
          }
          arrays[arrayName].forEach((item) => {
            const searchCondition = searchForMirrorItem ? item.ref_id_mirror === id || item.row_id === id : item.row_id === id
            if (searchCondition && !idsToDelete.has(item.row_id)) {
              // Mark this item for deletion
              idsToDelete.add(item.row_id)
              // Recursively find items that reference this item
              findReferencesToDelete(item.row_id)
            }
          })
        })
      }

      // Start the recursive search with our target ID
      findReferencesToDelete(targetId)

      consola.debug('idsToDelete: ', idsToDelete)
      // Create new arrays without the deleted items
      const result: { [key: string]: StcsOrgFsdFlatItem[] } = { ...arrays }
      arrayNames.forEach((arrayName) => {
        result[arrayName] = arrays[arrayName].filter((item) => !idsToDelete.has(item.row_id))
      })

      return result
    }

    function deleteRows(id: string, currentFinancialStatement?: StcsFstmtEnum, arraysToSearch?: StcsFstmtEnum[]) {
      const result = deleteCascadingReferences(
        id,
        {
          [StcsFstmtEnum.SFSE_NONE]: [],
          [StcsFstmtEnum.SFSE_PNL]: pnlList.value,
          [StcsFstmtEnum.SFSE_BS]: bsList.value,
          [StcsFstmtEnum.SFSE_CF]: [],
          [StcsFstmtEnum.UNRECOGNIZED]: [],
        },
        currentFinancialStatement,
        arraysToSearch,
      )

      consola.debug(result)

      pnlList.value = result[StcsFstmtEnum.SFSE_PNL]
      bsList.value = result[StcsFstmtEnum.SFSE_BS]
    }

    function setStatementFsdList(statementFsdList: StcsOrgFsdFlatItem[], finStmt: StcsFstmtEnum): void {
      switch (finStmt) {
        case StcsFstmtEnum.SFSE_PNL:
          pnlList.value = statementFsdList
          break
        case StcsFstmtEnum.SFSE_BS:
          bsList.value = statementFsdList
          break
        default:
          return
      }
    }

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    function mergeFsdAndAccMapping(stcs_comp_acc_mapping: { [key: string]: StcsCompAccMappingL2 } | undefined, finStmt: StcsFstmtEnum): void {
      if (!stcs_comp_acc_mapping) {
        return
      }

      const listRefs: Record<StcsFstmtEnum, Ref<StcsOrgFsdFlatItem[]> | undefined> = {
        [StcsFstmtEnum.SFSE_NONE]: undefined,
        [StcsFstmtEnum.SFSE_PNL]: pnlList,
        [StcsFstmtEnum.SFSE_BS]: bsList,
        [StcsFstmtEnum.SFSE_CF]: undefined,
        [StcsFstmtEnum.UNRECOGNIZED]: undefined,
      }

      const fsdList = listRefs[finStmt]
      if (!fsdList) {
        return
      }

      const tempList: StcsOrgFsdFlatItem[] = []

      for (let i = fsdList.value.length - 1; i >= 0; i--) {
        const item = fsdList.value[i]

        // Add the original FSD item
        tempList.unshift({ ...item })

        if (item.row_id in stcs_comp_acc_mapping) {
          const acm_map_level_3: { [key: string]: StcsCompAccMappingLevel3 } = stcs_comp_acc_mapping[item.row_id].acm_map_level_3

          for (const key in acm_map_level_3) {
            if (Object.prototype.hasOwnProperty.call(acm_map_level_3, key)) {
              let rowId = key

              if (finStmt === StcsFstmtEnum.SFSE_BS) {
                rowId = rephaseDuplicatedkey(key, item.row_id)
              }

              const accMapFlatItem: StcsOrgFsdFlatItem = {
                ...item,
                row_id: rowId,
                path_level: [...item.path_level, key],
                rowClass: 'account-item',
                fpna_type: StcsFpnaTypeEnum.SFT_ACC_ITEM,
                options: undefined,
              }

              tempList.unshift(accMapFlatItem)
            }
          }
        }
      }

      fsdList.value = tempList
    }

    async function fetchAccMappingData(successCb?: (response: GenericPayloadResponse) => void, errorCb?: () => void) {
      const ctxStore = useCtxStore()
      const orgId = ctxStore.active_org_id.extractId()
      const compId = ctxStore.active_comp_id.extractId()
      const orgMap = ctxStore.ref_org_map[orgId]

      if (!orgMap?.onboarding?.ref_id_comp_onboarding) {
        errorCb?.()
        return
      }

      const accMappingRequest: DmCompAcmGetRequest = {
        ref_id_comp: compId,
        ref_id_org: orgId,
        ref_id_user: ctxStore.ref_user.id.extractId(),
      }

      let response: GenericPayloadResponse

      response = await firstValueFrom(cmwDmCompAcmGetDraftRequest(accMappingRequest))
      consola.debug('cmwDmCompAcmGetDraftRequest: ', response)

      if (response.empty) {
        response = await firstValueFrom(cmwDmCompAcmGetRequest(accMappingRequest))
        consola.debug('cmwDmCompAcmGetRequest: ', response)
      }

      switch (response.res_code) {
        case StcsResCode.SUCCESS: {
          loadAccMappingData(response.stcs_comp_acc_mapping)
          successCb?.(response)
          break
        }
        default:
          errorCb?.()
          break
      }
    }

    function loadAccMappingData(response: StcsCompAccMapping | undefined): void {
      if (!response) {
        return
      }

      Object.assign(accMappingData, response)
      bsMapList.value = convertL3UnspecifiedToDebitCredit(response.bs)
      pnlMapList.value = response.pnl
      i18n_map.value = response.i18n_map
      isdraft.value = response.is_draft
      i18n_custom_key.value = response.i18n_custom_key
      mergeFsdAndAccMapping(response.pnl?.acm_map_level_2, StcsFstmtEnum.SFSE_PNL)
      mergeFsdAndAccMapping(response.bs?.acm_map_level_2, StcsFstmtEnum.SFSE_BS)
      lastActionMapList.value = {
        activeIndex: 0,
        lastAction: [captureState()],
      }
    }

    function updateLastActionMapList() {
      if (!lastActionMapList.value) {
        return
      }

      if (lastActionMapList.value.activeIndex < 0) {
        return
      }

      lastActionMapList.value.activeIndex++

      const lastAction = lastActionMapList.value.lastAction.slice(0, lastActionMapList.value.activeIndex + 1)

      lastActionMapList.value.lastAction = [...lastAction, captureState()]
    }

    function undoLastAction() {
      if (!lastActionMapList.value) {
        return
      }

      const activeIndex = lastActionMapList.value.activeIndex

      if (activeIndex === 0) {
        return
      }

      const lastAction = lastActionMapList.value.lastAction
      const lastActionItem = deepClone(lastAction[activeIndex - 1])

      lastActionMapList.value.activeIndex--
      applyState(lastActionItem)
    }

    function redoLastAction() {
      if (!lastActionMapList.value) {
        return
      }

      const activeIndex = lastActionMapList.value.activeIndex
      if (activeIndex === lastActionMapList.value.lastAction.length - 1) {
        return
      }

      const lastAction = lastActionMapList.value.lastAction
      const lastActionItem = deepClone(lastAction[activeIndex + 1])

      lastActionMapList.value.activeIndex++
      applyState(lastActionItem)
    }

    function applyState(snapshot: MapHistoryItem): void {
      pnlList.value = deepClone(snapshot.pnlList)
      bsList.value = deepClone(snapshot.bsList)
      pnlMapList.value = deepClone(snapshot.pnlMapList)
      bsMapList.value = deepClone(snapshot.bsMapList)
      i18n_map.value = deepClone(snapshot.i18n_map)

      updateAccMappingData(
        () => null,
        () => null,
        false,
      )
    }

    function captureState(): MapHistoryItem {
      return {
        pnlList: deepClone(pnlList.value),
        bsList: deepClone(bsList.value),
        pnlMapList: deepClone(pnlMapList.value ?? ({} as StcsCompAccMappingFstmt)),
        bsMapList: deepClone(bsMapList.value ?? ({} as StcsCompAccMappingFstmt)),
        i18n_map: deepClone(i18n_map.value),
      }
    }

    function updateAccMappingData(successCb?: () => void, errorCb?: () => void, updateLastAction = true) {
      const ctxStore = useCtxStore()
      const orgId = ctxStore.active_org_id.extractId()
      const orgMap = ctxStore.ref_org_map[orgId]

      if (!orgMap?.onboarding?.ref_id_comp_onboarding) {
        errorCb?.()
        return
      }

      isdraft.value = true
      const ref_id_comp_onboarding = orgMap.onboarding.ref_id_comp_onboarding
      const accMappingUpdateRequest: DmCompAcmSaveDraftRequest = {
        ref_id_comp: ref_id_comp_onboarding,
        ref_id_org: orgId,
        ref_id_user: ctxStore.ref_user.id.extractId(),
        comp_acc_mapping: { ...accMappingData, pnl: pnlMapList.value, bs: bsMapList.value, i18n_map: i18n_map.value },
      }

      if (updateLastAction) {
        updateLastActionMapList?.()
      }

      cmwDmCompAcmSaveDraftRequest(accMappingUpdateRequest).subscribe((response: GenericPayloadResponse) => {
        switch (response.res_code) {
          case StcsResCode.SUCCESS:
            successCb?.()
            break
          default:
            errorCb?.()
            break
        }
      })
    }

    function onBoardingConfirmAccMappingData(successCb?: (response: MultiGenericPayloadResponse) => void, errorCb?: () => void) {
      const ctxStore = useCtxStore()
      const accMappingConfirmRequest: OnboardingCompleteAccMappingRequest = {
        ref_id_org: ctxStore.active_org_id,
        ref_id_comp: ctxStore.active_comp_id,
        ref_id_user: ctxStore.ref_user.id.extractId(),
      }
      isdraft.value = false

      cmwOnboardingCompleteAccMappingRequest(accMappingConfirmRequest).subscribe((response: MultiGenericPayloadResponse) => {
        consola.debug('cmwOnboardingCompleteAccMappingRequest: ', response)
        switch (response.res_code) {
          case StcsResCode.SUCCESS:
            successCb?.(response)
            break
          default:
            errorCb?.()
            break
        }
      })
    }

    function onDmConfirmAccMappingData(successCb?: (response: GenericPayloadResponse) => void, errorCb?: () => void) {
      const ctxStore = useCtxStore()
      const orgId = ctxStore.active_org_id.extractId()

      isdraft.value = false
      const accMappingConfirmRequest: DmCompAcmConfirmRequest = {
        ref_id_comp: route?.params.compId as string,
        ref_id_org: orgId,
        ref_id_user: ctxStore.ref_user.id.extractId(),
      }

      cmwDmCompAcmConfirmRequest(accMappingConfirmRequest).subscribe((response: GenericPayloadResponse) => {
        consola.debug('cmwDmCompAcmConfirmRequest: ', response)
        switch (response.res_code) {
          case StcsResCode.SUCCESS:
            successCb?.(response)
            break
          default:
            errorCb?.()
            break
        }
      })
    }

    function deleteALlTags(successCb?: () => void, errorCb?: () => void) {
      const listMaps: Record<StcsFstmtEnum, StcsCompAccMappingFstmt | undefined> = {
        [StcsFstmtEnum.SFSE_NONE]: undefined,
        [StcsFstmtEnum.SFSE_PNL]: pnlMapList.value,
        [StcsFstmtEnum.SFSE_BS]: bsMapList.value,
        [StcsFstmtEnum.SFSE_CF]: undefined,
        [StcsFstmtEnum.UNRECOGNIZED]: undefined,
      }

      const selectedMap = listMaps[activeFinancialStatement.value]

      if (!selectedMap) {
        return undefined
      }

      updateAllLevel3Ids(selectedMap?.acm_map_level_2 ?? {}, 'level_3_other')

      updateAccMappingData(successCb, errorCb)
    }

    function getContextMenuOptions(params: GetContextMenuItemsParams, financialStatement: StcsFstmtEnum): (DefaultMenuItem | MenuItemDef)[] | Promise<(DefaultMenuItem | MenuItemDef)[]> {
      const currentStatement = financialStatement === StcsFstmtEnum.SFSE_PNL ? StcsFstmtEnum.SFSE_PNL : StcsFstmtEnum.SFSE_BS
      const otherStatement = currentStatement === StcsFstmtEnum.SFSE_PNL ? StcsFstmtEnum.SFSE_BS : StcsFstmtEnum.SFSE_PNL

      const buildStatementMenuItems = (list: StcsOrgFsdFlatItem[], selectedStatement: StcsFstmtEnum) => buildMenuFromTree(buildHierarchy(list), params, false, selectedStatement, currentStatement)

      const STATEMENT_ICONS = {
        [StcsFstmtEnum.SFSE_PNL]: '<i class="bx bx-bar-chart-square"></i>',
        [StcsFstmtEnum.SFSE_BS]:
          '<svg data-v-9481e513="" class="svg-bs" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 4.11768H13.1765V5.76473H0V4.11768ZM0 8.23532H13.1765V9.88238H0V8.23532ZM0 12.353H13.1765V14H0V12.353Z" fill="currentColor"/><path d="M0 0L9.47059 0V1.64706H0V0Z" fill="currentColor"/></svg>',
      }
      const STATEMENT_DISPLAY_NAMES = {
        [StcsFstmtEnum.SFSE_PNL]: $tolgee.t('general-terms-pnl'),
        [StcsFstmtEnum.SFSE_BS]: $tolgee.t('general-terms-bs'),
      }
      const menuItems = {
        [StcsFstmtEnum.SFSE_PNL]: buildStatementMenuItems(pnlList.value, StcsFstmtEnum.SFSE_PNL),
        [StcsFstmtEnum.SFSE_BS]: buildStatementMenuItems(bsList.value, StcsFstmtEnum.SFSE_BS),
      }

      const result: (DefaultMenuItem | MenuItemDef)[] = [
        {
          name: $tolgee.t('move-to'),
          subMenu: menuItems[currentStatement],
          cssClasses: ['move-to-current-statement'],
        },
      ]

      if (currentStatement === StcsFstmtEnum.SFSE_PNL || (currentStatement === StcsFstmtEnum.SFSE_BS && checkNoteIsDebitCredit(params.node?.data))) {
        result.push({
          name: `${$tolgee.t('move-to')} ${STATEMENT_DISPLAY_NAMES[otherStatement]}`,
          icon: STATEMENT_ICONS[otherStatement],
          subMenu: menuItems[otherStatement],
          cssClasses: [`move-to-${otherStatement === StcsFstmtEnum.SFSE_BS ? 'bs' : 'pnl'}`],
        })
      }

      return result
    }

    const TOTAL_TYPES = new Set([StcsFpnaTypeEnum.SFT_TOTAL_CML, StcsFpnaTypeEnum.SFT_TOTAL_NCML, StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH])

    function shouldDisableForDebitOrCredit(node: CustomTreeNode, params: GetContextMenuItemsParams, currentStatement: StcsFstmtEnum): boolean {
      if (currentStatement === StcsFstmtEnum.SFSE_BS && !checkNoteIsDebitCredit(params.node?.data)) {
        if (checkNodeIsDebitOrCredit({ data: node.data } as IRowNode) !== checkNodeIsDebitOrCredit(params.node as IRowNode)) {
          return true
        }
      }

      return false
    }

    function buildMenuFromTree(nodes: CustomTreeNode[], params: GetContextMenuItemsParams, isNested: boolean, selectedStatement: StcsFstmtEnum, currentStatement: StcsFstmtEnum): MenuItemDef[] {
      return nodes
        .filter((node) => {
          // Skip these types
          return ![StcsFpnaTypeEnum.SFT_ACC_ITEM, StcsFpnaTypeEnum.SFT_CTX_LINE_IPO].includes(node.data.fpna_type)
        })
        .map((node) => {
          const menuItem: MenuItemDef = {
            name: node.label || getI18nDisplayName(node.data).name,
            cssClasses: [`menu-item-${node.data.row_id}`],
          }

          if (TOTAL_TYPES.has(node.data.fpna_type) || shouldDisableForDebitOrCredit(node, params, currentStatement)) {
            menuItem.disabled = true
          }

          if (node.children.length > 0 && [StcsFpnaTypeEnum.SFT_LEVEL_1, StcsFpnaTypeEnum.SFT_SUB_TOTAL].includes(node.data.fpna_type)) {
            menuItem.subMenu = buildMenuFromTree(node.children, params, true, selectedStatement, currentStatement)
          }

          if (isNested && [StcsFpnaTypeEnum.SFT_LEVEL_2].includes(node.data.fpna_type)) {
            menuItem.action = () => {
              setAccMappingData(node.data, params, selectedStatement, currentStatement)
              updateAccMappingData()
            }
          }

          return menuItem
        })
    }

    function setAccMappingData(tragetNode: StcsOrgFsdFlatItem, params: GetContextMenuItemsParams | RowDragEndEvent, selectedStatement: StcsFstmtEnum, currentStatement: StcsFstmtEnum) {
      const listRefs: Record<StcsFstmtEnum, Ref<StcsOrgFsdFlatItem[]> | undefined> = {
        [StcsFstmtEnum.SFSE_NONE]: undefined,
        [StcsFstmtEnum.SFSE_PNL]: pnlList,
        [StcsFstmtEnum.SFSE_BS]: bsList,
        [StcsFstmtEnum.SFSE_CF]: undefined,
        [StcsFstmtEnum.UNRECOGNIZED]: undefined,
      }

      // Get the target list reference and value
      const targetRef = listRefs[selectedStatement]

      if (!targetRef?.value) {
        return
      }

      const selectedData = params.api.getSelectedRows()
      const isSelected = params?.node?.isSelected()

      const selectedNodes = isSelected ? selectedData : [params.node?.data]

      selectedNodes.forEach((selectedNode) => {
        const updatedNode = { ...selectedNode, path_level: [...tragetNode.path_level, selectedNode.row_id] }
        if (selectedStatement === currentStatement) {
          // update the mapping list and remove from the other list
          updateCurrentMapListAndRemoveFromOther(tragetNode.row_id, selectedNode.row_id, selectedNode?.path_level.at(-2) ?? '', selectedStatement)

          const selectedNodeIndex = targetRef.value?.indexOf(selectedNode)
          // update the selected node
          targetRef.value.splice(selectedNodeIndex, 1, updatedNode)
        } else if (selectedStatement === StcsFstmtEnum.SFSE_PNL) {
          // update PNL list and remove from BS list
          updatePnlMapListAndRemoveFromBs(tragetNode.row_id, selectedNode.row_id, selectedNode.path_level[2])
          // update P&L list
          pnlList.value.push(updatedNode)
          // remove from BS list
          const bsNodeIndex = bsList.value.findIndex((item) => item.row_id === selectedNode.row_id)
          bsList.value.splice(bsNodeIndex, 1)
        } else if (selectedStatement === StcsFstmtEnum.SFSE_BS) {
          // update BS list and remove from PNL list
          updateBsMapListAndRemoveFromPnl(tragetNode.row_id, selectedNode.row_id, selectedNode.path_level[1])
          // update BS list
          bsList.value.push(updatedNode)
          // remove from PNL list
          const pnlNodeIndex = pnlList.value.findIndex((item) => item.row_id === selectedNode.row_id)
          pnlList.value.splice(pnlNodeIndex, 1)
        }
      })
      params.api.deselectAll()
    }

    function updateAllLevel3Ids(data: { [key: string]: StcsCompAccMappingL2 }, value: string): void {
      for (const section of Object.values(data)) {
        const acmMap: { [key: string]: StcsCompAccMappingLevel3 } = section.acm_map_level_3 || {}
        for (const item of Object.values(acmMap)) {
          if (Object.prototype.hasOwnProperty.call(item, 'level_3_id')) {
            item.level_3_id = value
          }
        }
      }
    }

    function getI18nDisplayName(item: StcsOrgFsdFlatItem): { name: string; isAlternate: boolean } {
      // Return default display name if item is undefined
      if (!item) {
        return { name: '', isAlternate: false }
      }

      const displayName = fsdStore.getDisplayNameBylevelId(item)

      if (item.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM) {
        displayName.name = `${removeDebitCreditFromKey(item.row_id)} ${i18n_map.value[accMappingData.i18n_custom_key]?.label_map?.[removeDebitCreditFromKey(item.row_id)]}`
      }

      return displayName
    }

    return {
      isFinancialStatementActive,
      setFinancialStatement,
      updateAccMappingData,
      deleteALlTags,
      getI18nDisplayName,
      onBoardingConfirmAccMappingData,
      onDmConfirmAccMappingData,
      fetchAccMappingData,
      setStatementFsdList,
      setAccMappingData,
      loadAccMappingData,
      pnlList,
      bsList,
      isdraft,
      isDataReady,
      pnlActionButtonStrategies,
      bsActionButtonStrategies,
      i18n_map,
      pnlMapList,
      mode,
      bsMapList,
      activeFinancialStatement,
      lastActionMapList,
      getContextMenuOptions,
      addRow,
      deleteRows,
      redoLastAction,
      undoLastAction,
      getMappingItem,
      getMappingLists,
      i18n_custom_key,
      nonCatFlag,
    }
  },
  { persist: false },
)

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}
